import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, Download, Upload, ImageIcon, X, RotateCcw, Check,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import Logo from "@/components/Logo";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import formatDoctorName from "@/lib/names";

// Must match the constants in backend/lama.py.
const TEMPLATE = "/lama-01.png";
const TEMPLATE_RATIO = 2550 / 5550;   // height / width
// The ring: centre x and radius as fractions of width, centre y of height.
const RING_CX = 0.7770;
const RING_CY = 0.5033;
const RING_R = 0.1934;
// Name block, right-aligned to the end of the red rule.
const TEXT_RIGHT = 0.5331;
const TEXT_LEFT = 0.0300;
const NAME_Y = 0.3883;
const NAME_SIZE = 0.0457;   // fractions of template WIDTH
const SUB_Y = 0.5300;
const SUB_SIZE = 0.0256;
const DEFAULT_POS = { dx: 0.0, dy: 0.0, scale: 1.0 };
const DEFAULT_SIZES = { name: 1.0, sub: 1.0 };

// Mirrors the backend's fit_font: shrink a long name so it stays on one line.
function fitNamePx(name, px, availPx) {
  const canvas = fitNamePx.canvas || (fitNamePx.canvas = document.createElement("canvas"));
  const ctx = canvas.getContext("2d");
  const floor = px * 0.55;
  let size = px;
  for (;;) {
    ctx.font = `bold ${size}px Arial, Helvetica, sans-serif`;
    if (ctx.measureText(name).width <= availPx || size * 0.95 < floor) return size;
    size = size * 0.95;
  }
}

// Mirrors the backend's naming rule so the preview shows the final text.
function displayName(raw) {
  const n = (raw || "").trim();
  if (!n) return "";
  if (!n.toLowerCase().startsWith("dr")) return `Dr. ${n}`;
  if (n.toLowerCase().startsWith("dr ")) return `Dr.${n.slice(2)}`;
  return n;
}

export default function LamaStudio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const stageRef = useRef(null);
  const fileRef = useRef(null);

  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");     // object URL or server URL
  const [photoFile, setPhotoFile] = useState(null); // only set when newly chosen
  const [natural, setNatural] = useState(null);     // { w, h } of the photo
  const [pos, setPos] = useState(DEFAULT_POS);
  const [sizes, setSizes] = useState(DEFAULT_SIZES);
  const [fields, setFields] = useState({ doctor_name: "", qualification: "" });
  const [drag, setDrag] = useState(null);
  const [stageW, setStageW] = useState(640);

  // Font sizes are fractions of template width, so the preview needs its own width.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setStageW(e.contentRect.width));
    ro.observe(el);
    setStageW(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, [loading]);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/submissions/${id}`)
      .then(({ data }) => {
        if (!alive) return;
        setSub(data);
        // Same fallback as the backend's lama_settings: the lama studio's own
        // settings once saved, the standee's until then.
        if (data.lama_saved_at) {
          setFields({
            doctor_name: formatDoctorName(data.lama_doctor_name || data.doctor_name) || "",
            qualification: data.lama_qualification || data.qualification || "",
          });
          setPos({
            dx: data.lama_photo_dx || 0, dy: data.lama_photo_dy || 0,
            scale: data.lama_photo_scale || 1,
          });
          setSizes({ name: data.lama_name_scale || 1, sub: data.lama_sub_scale || 1 });
        } else {
          setFields({
            doctor_name: formatDoctorName(data.standee_doctor_name || data.doctor_name) || "",
            qualification: data.standee_qualification || data.qualification || "",
          });
          setPos({
            dx: data.standee_photo_dx || 0, dy: data.standee_photo_dy || 0,
            scale: data.standee_photo_scale || 1,
          });
        }
        if (data.lama_photo || data.standee_photo || data.doctor_photo) {
          const token = localStorage.getItem("bc_token");
          setPhotoUrl(`${API}/admin/submissions/${id}/lama/photo?auth=${token}&t=${Date.now()}`);
        }
      })
      .catch((err) => {
        if (alive) toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load submission");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Image must be under 8MB"); return; }
    setPhotoFile(f);
    setPhotoUrl((old) => { if (old?.startsWith("blob:")) URL.revokeObjectURL(old); return URL.createObjectURL(f); });
    setNatural(null);
  };

  // Drag moves the photo inside the ring, in units of the ring radius, which is
  // what the renderer uses, so the preview and the PNG agree.
  const startDrag = (e) => {
    if (!photoUrl) return;
    e.preventDefault();
    const r = stageRef.current.getBoundingClientRect();
    setDrag({ x: e.clientX, y: e.clientY, a: pos.dx, b: pos.dy, w: r.width });
  };
  const onPointerMove = useCallback((e) => {
    if (!drag) return;
    const radius = drag.w * RING_R;
    const a = Math.min(2, Math.max(-2, drag.a + (e.clientX - drag.x) / radius));
    const b = Math.min(2, Math.max(-2, drag.b + (e.clientY - drag.y) / radius));
    setPos((p) => ({ ...p, dx: a, dy: b }));
  }, [drag]);

  useEffect(() => {
    if (!drag) return;
    const move = (e) => onPointerMove(e);
    const up = () => setDrag(null);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [drag, onPointerMove]);

  // Mirror the backend's circle_photo in stage pixels.
  const previewBox = () => {
    if (!natural) return null;
    const radius = stageW * RING_R;
    const k = ((2 * radius) / Math.min(natural.w, natural.h)) * pos.scale;
    const w = natural.w * k;
    const h = natural.h * k;
    return {
      w, h,
      left: radius + pos.dx * radius - w / 2,
      top: radius + pos.dy * radius - h / 2,
    };
  };

  const save = async () => {
    if (!photoUrl) { toast.error("Please choose a doctor photo first."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      if (photoFile) fd.append("file", photoFile);
      fd.append("doctor_name", fields.doctor_name);
      fd.append("qualification", fields.qualification);
      fd.append("photo_dx", String(pos.dx));
      fd.append("photo_dy", String(pos.dy));
      fd.append("photo_scale", String(pos.scale));
      fd.append("name_scale", String(sizes.name));
      fd.append("sub_scale", String(sizes.sub));
      await api.post(`/admin/submissions/${id}/lama`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSub((s) => ({ ...s, lama_saved_at: new Date().toISOString() }));
      setPhotoFile(null);
      toast.success("Lama saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save the lama");
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    const token = localStorage.getItem("bc_token");
    window.open(`${API}/admin/submissions/${id}/lama?auth=${token}`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F8F9FA]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0B5351]" />
      </div>
    );
  }
  if (!sub) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F8F9FA] px-6">
        <div className="text-center">
          <p className="font-heading font-bold text-slate-900 mb-4">Submission not found</p>
          <Button onClick={() => navigate("/admin")} className="rounded-xl bg-[#0B5351] h-11 px-6">Back</Button>
        </div>
      </div>
    );
  }

  const box = previewBox();
  const shownName = displayName(fields.doctor_name);
  const availPx = (TEXT_RIGHT - TEXT_LEFT) * stageW;
  const namePx = fitNamePx(shownName, NAME_SIZE * sizes.name * stageW, availPx);
  const textBox = {
    position: "absolute", pointerEvents: "none", textAlign: "right",
    left: `${TEXT_LEFT * 100}%`, width: `${(TEXT_RIGHT - TEXT_LEFT) * 100}%`,
    fontFamily: "Arial, Helvetica, sans-serif",
  };
  const sizeSlider = (key, label) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <span className="text-xs text-slate-500 tabular-nums">{Math.round(sizes[key] * 100)}%</span>
      </div>
      <Slider data-testid={`lama-${key}-size`} min={50} max={160} step={1}
        value={[Math.round(sizes[key] * 100)]}
        onValueChange={([v]) => setSizes((s) => ({ ...s, [key]: v / 100 }))} />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Logo className="h-9 w-9" />
            <div className="min-w-0">
              <p className="font-heading font-bold text-slate-900 leading-none truncate">Lama Studio</p>
              <p className="text-xs text-slate-500 truncate">{formatDoctorName(sub.doctor_name)}</p>
            </div>
          </div>
          <Button data-testid="lama-back" onClick={() => navigate("/admin")} variant="outline" size="sm"
            className="rounded-lg h-11 px-3 shrink-0">
            <ArrowLeft className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Back</span>
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-8 items-start">
        {/* Preview */}
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Preview</p>
          <h1 className="font-heading text-2xl font-black tracking-tighter text-slate-900 mb-1">
            Drag to position
          </h1>
          <p className="text-slate-500 mb-4 text-sm">
            Drag the photo inside the ring and set the name and degree sizes; the downloaded lama is rendered from exactly these settings.
          </p>
          <div
            ref={stageRef}
            data-testid="lama-stage"
            className="relative mx-auto w-full max-w-[720px] rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm select-none"
            style={{ aspectRatio: `1 / ${TEMPLATE_RATIO}` }}
          >
            <img src={TEMPLATE} alt="Lama template" className="absolute inset-0 w-full h-full" draggable={false} />
            {shownName && (
              <div data-testid="lama-stage-name" style={{
                ...textBox, top: `${NAME_Y * 100}%`,
                color: "#1e347d", fontWeight: "bold", lineHeight: 1.05, fontSize: `${namePx}px`,
              }}>
                {shownName}
              </div>
            )}
            {fields.qualification && (
              <div data-testid="lama-stage-qualification" style={{
                ...textBox, top: `${SUB_Y * 100}%`,
                color: "#1e347d", lineHeight: 1.25, fontSize: `${SUB_SIZE * sizes.sub * stageW}px`,
              }}>
                {fields.qualification}
              </div>
            )}

            {/* The ring: the photo is clipped to the ring's inner edge, as the renderer does. */}
            {photoUrl && (
              <div
                data-testid="lama-stage-ring"
                onPointerDown={startDrag}
                className={`absolute overflow-hidden rounded-full ring-1 ring-transparent hover:ring-[#0B5351]/40 ${drag ? "cursor-grabbing" : "cursor-grab"}`}
                style={{
                  left: `${(RING_CX - RING_R) * 100}%`,
                  top: `${RING_CY * 100}%`,
                  width: `${RING_R * 2 * 100}%`,
                  aspectRatio: "1 / 1",
                  transform: "translateY(-50%)",
                }}
              >
                <img
                  src={photoUrl}
                  alt="Doctor"
                  data-testid="lama-stage-photo"
                  draggable={false}
                  onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                  className="absolute max-w-none pointer-events-none"
                  style={box ? {
                    width: `${box.w}px`,
                    height: `${box.h}px`,
                    left: `${box.left}px`,
                    top: `${box.top}px`,
                  } : { visibility: "hidden" }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
            <div className="space-y-2">
              <Label>Doctor Photo</Label>
              <input ref={fileRef} type="file" accept="image/*" onChange={pickFile}
                className="hidden" data-testid="lama-file-input" />
              {photoUrl ? (
                <div className="flex items-center gap-2">
                  <Button variant="outline" data-testid="lama-change-photo"
                    onClick={() => fileRef.current?.click()} className="rounded-lg h-11 flex-1">
                    <Upload className="h-4 w-4 mr-2" /> Change photo
                  </Button>
                  <Button variant="outline" size="sm" data-testid="lama-clear-photo"
                    onClick={() => { setPhotoUrl(""); setPhotoFile(null); setNatural(null); if (fileRef.current) fileRef.current.value = ""; }}
                    className="rounded-lg h-11 px-3">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <button type="button" data-testid="lama-pick-photo" onClick={() => fileRef.current?.click()}
                  className="w-full h-28 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 hover:border-[#0B5351] hover:bg-[#0B5351]/5 grid place-items-center transition-colors">
                  <div className="text-center">
                    <ImageIcon className="h-6 w-6 text-slate-400 mx-auto mb-1.5" />
                    <p className="text-sm font-medium text-slate-600">Upload doctor photo</p>
                  </div>
                </button>
              )}
              <p className="text-xs text-slate-500">Changing the photo here affects only the lama, not the standee.</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Photo size</Label>
                <span className="text-xs text-slate-500 tabular-nums">{Math.round(pos.scale * 100)}%</span>
              </div>
              <Slider data-testid="lama-photo-size" min={100} max={300} step={1}
                value={[Math.round(pos.scale * 100)]}
                onValueChange={([v]) => setPos((p) => ({ ...p, scale: v / 100 }))} />
            </div>
            {sizeSlider("name", "Name size")}
            {sizeSlider("sub", "Degree size")}

            <Button variant="outline" data-testid="lama-reset" className="w-full rounded-lg h-10"
              onClick={() => { setPos(DEFAULT_POS); setSizes(DEFAULT_SIZES); }}>
              <RotateCcw className="h-4 w-4 mr-2" /> Reset position and sizes
            </Button>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ldn">Doctor Name</Label>
              <Input id="ldn" data-testid="lama-doctor-name" className="h-11 bg-slate-50"
                value={fields.doctor_name}
                onChange={(e) => setFields({ ...fields, doctor_name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ldq">Degree / Qualification</Label>
              <Input id="ldq" data-testid="lama-qualification" className="h-11 bg-slate-50"
                value={fields.qualification}
                onChange={(e) => setFields({ ...fields, qualification: e.target.value })} />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button data-testid="lama-save" onClick={save} disabled={busy || !photoUrl}
              className="w-full rounded-xl h-12 bg-[#0B5351] hover:bg-[#094241] font-semibold">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Check className="h-4 w-4 mr-2" /> Save Lama</>}
            </Button>
            <Button variant="outline" data-testid="lama-download" onClick={download}
              className="w-full rounded-xl h-12">
              <Download className="h-4 w-4 mr-2" /> Download {sub.lama_saved_at ? "lama" : "default lama"}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
